import { useState } from 'react';
import { CourseEvaluationClient, CourseEvaluationDTO, CourseEvaluationSubmissionDTO, CheckExamineeIdRequestDTO, ApiResponse_string, ApiResponse_courseEvaluationDTO, ApiResponse_courseEvaluationSubmissionResultDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useCourseEvaluationApi = () => {
    const apiClient = new CourseEvaluationClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: CourseEvaluationDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.createCourseEvaluation(data);
        } catch (err: any) {
            setError(err.message || 'Error creating CourseEvaluation');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: CourseEvaluationDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateCourseEvaluation(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating CourseEvaluation');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getCourseEvaluationById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching CourseEvaluation');
        } finally {
            setIsLoading(false);
        }
    };

    const getByCourseId = async (id: string): Promise<ApiResponse_courseEvaluationDTO | undefined> => {
        setIsLoading(true);
        try {
            return await apiClient.getCourseEvaluationByCourseId(id) as ApiResponse_courseEvaluationDTO;
        } catch (err: any) {
            setError(err.message || 'Error fetching CourseEvaluation');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchCourseEvaluations(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching CourseEvaluations');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteCourseEvaluation(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting CourseEvaluation');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportCourseEvaluations(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting CourseEvaluations', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const submit = async (
        data: CourseEvaluationSubmissionDTO
    ): Promise<ApiResponse_courseEvaluationSubmissionResultDTO> => {
        setIsLoading(true);
        try {
            return await apiClient.submitCourseEvaluation(data) as ApiResponse_courseEvaluationSubmissionResultDTO;
        } catch (err: unknown) {
            handleHookError(err, 'Error submitting CourseEvaluation', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const extractDuplicates = async (
        fromDate: string,
        toDate: string
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportDuplicateCookieGuids({
                filters: {
                    createdFrom: new Date(fromDate),
                    createdTo: new Date(toDate),
                },
                page: 1,
                pageSize: 10,
            });
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting duplicate examinee IDs', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const checkExamineeId = async (data: CheckExamineeIdRequestDTO) => {
        try {
            return await apiClient.checkExamineeId(data);
        } catch (err: unknown) {
            handleHookError(err, 'Error checking examinee ID', setError);
            throw err;
        }
    };

    return { create, edit, getById, getByCourseId, search, extract, extractDuplicates, checkExamineeId, remove, submit, isLoading, error };
};